'use client'

import {
  Check,
  CheckIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
} from 'lucide-react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'

import * as Icons from '@/components/icons'
import { Button, Input, Separator } from '@/components/ui'

import {
  type LocalAttachment,
  WorkflowAttachment,
} from './workflowAttachment/workflowAttachment'
import {
  type WorkflowGateStep,
  WorkflowGates,
  type WorkflowGateTask,
  type WorkflowGateTaskResponsible,
  type WorkflowViewGatesProps,
} from './workflowGate'
import { WorkflowTaskView } from './workflowTask'

type WorkflowViewHeaderProps = {
  title: string
  onTitleChange?: (newName: string) => void
  onDelete?: () => void
}

function WorkflowViewHeader({
  title,
  onTitleChange,
  onDelete,
}: WorkflowViewHeaderProps) {
  const isIdeaMode = onTitleChange === undefined && onDelete === undefined
  const [isEditing, setIsEditing] = useState(false)
  const [value, setValue] = useState(title)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => setValue(title), [title])
  useEffect(() => {
    if (isEditing) inputRef.current?.focus()
  }, [isEditing])

  const save = () => {
    const trimmed = value.trim()
    if (trimmed && onTitleChange) onTitleChange(trimmed)
    else setValue(title)
    setIsEditing(false)
  }

  const cancel = () => {
    setValue(title)
    setIsEditing(false)
  }

  return (
    <header className="flex items-center gap-2 rounded-t-lg">
      <div className="flex w-fit items-center justify-center gap-2 rounded-t-lg bg-system-background-grey px-3 py-2">
        <Icons.GitBranch className="size-6 shrink-0 text-system-primary-icon" />
        {!isIdeaMode && isEditing ? (
          <Input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save()
              if (e.key === 'Escape') cancel()
            }}
            className="h-8 w-[60ch] max-w-full border-system-border-secondary font-inter text-sm font-semibold leading-[1.21] text-system-text-primary"
            maxLength={60}
          />
        ) : (
          <span className="font-inter text-sm font-semibold leading-[1.21] text-system-text-primary">
            {title}
          </span>
        )}
      </div>
      {!isIdeaMode && (
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            size="icon"
            variant="secondary"
            onClick={isEditing ? save : () => setIsEditing(true)}
            className="size-8 rounded-md border border-system-border-secondary bg-system-background-base"
          >
            {isEditing ? (
              <CheckIcon className="size-4 text-system-primary-icon" />
            ) : (
              <PencilIcon className="size-4 text-system-primary-icon" />
            )}
          </Button>
          {onDelete && (
            <Button
              type="button"
              size="icon"
              variant="secondary"
              onClick={onDelete}
              className="size-8 rounded-md border border-system-state-error bg-system-background-base text-system-state-error"
            >
              <Trash2Icon className="size-4" />
            </Button>
          )}
        </div>
      )}
    </header>
  )
}

type SelectedStepContentProps = {
  step: WorkflowGateStep
  stepIndex: number
  onUpdateStepName: ((index: number, name: string) => void) | undefined
  onCreateTask: () => void
  onUpdateTask: (taskIndex: number, name: string) => void
  onDeleteTask: (taskIndex: number) => void
  onUpdateTaskEndDate?: (taskIndex: number, endDate: Date | undefined) => void
  onUpdateTaskResponsible?: (
    taskIndex: number,
    responsible: WorkflowGateTaskResponsible | undefined,
  ) => void
  onReorderTasks: (reorderedTasks: WorkflowGateTask[]) => void
  onAddAttachments?: (files: LocalAttachment[]) => void
  onRemoveAttachment?: (index: number) => void
  enableAttachments?: boolean
  enableTaskResponsible?: boolean
  enableTaskEndDate?: boolean
  areaIds?: string[]
}

function SelectedStepContent({
  step,
  stepIndex,
  onUpdateStepName,
  onCreateTask,
  onUpdateTask,
  onDeleteTask,
  onUpdateTaskEndDate,
  onUpdateTaskResponsible,
  onReorderTasks,
  onAddAttachments,
  onRemoveAttachment,
  enableAttachments = true,
  enableTaskResponsible = true,
  enableTaskEndDate = true,
  areaIds,
}: SelectedStepContentProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [value, setValue] = useState(step.name)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => setValue(step.name), [step.name])
  useEffect(() => {
    if (isEditing) inputRef.current?.focus()
  }, [isEditing])

  const save = () => {
    const trimmed = value.trim()
    if (trimmed && onUpdateStepName) onUpdateStepName(stepIndex, trimmed)
    else setValue(step.name)
    setIsEditing(false)
  }

  const cancel = () => {
    setValue(step.name)
    setIsEditing(false)
  }

  return (
    <div className="flex flex-col gap-2 px-6 pb-4">
      <div className="flex w-full items-start gap-2">
        {isEditing ? (
          <>
            <div className="flex w-full items-start gap-2 px-4">
              <Input
                ref={inputRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') save()
                  if (e.key === 'Escape') cancel()
                }}
                className="w-full border-system-primary-purple font-inter text-sm font-medium text-system-text-secondary"
                maxLength={60}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="social" onClick={save}>
                <Check className="size-4" />
                Salvar
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="text-system-state-error"
                onClick={cancel}
              >
                Cancelar
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex w-full items-center gap-2 px-4">
              <span
                className="group flex w-full cursor-pointer items-center gap-1.5 rounded-lg p-2 text-base text-system-text-secondary transition-colors hover:border hover:border-system-primary-purple"
                onClick={() => setIsEditing(true)}
              >
                {step.name}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={onCreateTask}
                className="p-0 outline-none"
              >
                <PlusIcon className="size-4 text-system-primary-purple" />
                <span className="text-sm font-medium text-system-primary-purple">
                  Criar nova tarefa
                </span>
              </Button>
            </div>
          </>
        )}
      </div>
      <Separator />
      <div className="flex flex-col gap-8">
        <WorkflowTaskView
          tasks={step.tasks ?? []}
          onUpdateTask={onUpdateTask}
          onDeleteTask={onDeleteTask}
          onUpdateTaskEndDate={onUpdateTaskEndDate}
          onUpdateTaskResponsible={onUpdateTaskResponsible}
          onReorderTasks={onReorderTasks}
          enableResponsible={enableTaskResponsible}
          enableEndDate={enableTaskEndDate}
          areaIds={areaIds}
        />
        {enableAttachments && onAddAttachments && onRemoveAttachment && (
          <WorkflowAttachment
            stepName={step.name}
            attachments={step.attachments ?? []}
            onAddAttachments={onAddAttachments}
            onRemoveAttachment={onRemoveAttachment}
          />
        )}
      </div>
    </div>
  )
}

type WorkflowViewProps = {
  title: string
  onTitleChange?: (newName: string) => void
  onDelete?: () => void
  onCreateStage?: () => void
  gates?: WorkflowViewGatesProps | null
  children?: React.ReactNode
  enableAttachments?: boolean
  enableTaskResponsible?: boolean
  enableTaskEndDate?: boolean
  handleCreateTask?: () => void
  handleUpdateTask?: (taskIndex: number, name: string) => void
  handleDeleteTask?: (taskIndex: number) => void
  handleAddAttachments?: (files: LocalAttachment[]) => void
  handleRemoveAttachment?: (index: number) => void
  handleUpdateTaskEndDate?: (
    taskIndex: number,
    endDate: Date | undefined,
  ) => void
  handleUpdateTaskResponsible?: (
    taskIndex: number,
    responsible: WorkflowGateTaskResponsible | undefined,
  ) => void
  handleReorderTasks?: (reorderedTasks: WorkflowGateTask[]) => void
  areaIds?: string[]
}

function WorkflowView({
  title,
  onTitleChange,
  onDelete,
  onCreateStage,
  gates,
  enableAttachments,
  enableTaskResponsible,
  enableTaskEndDate,
  handleCreateTask,
  handleUpdateTask,
  handleDeleteTask,
  handleAddAttachments,
  handleRemoveAttachment,
  handleUpdateTaskEndDate,
  handleUpdateTaskResponsible,
  handleReorderTasks,
  areaIds,
  children,
}: WorkflowViewProps) {
  const selectedIndex = gates?.selectedIndex ?? null
  const step = selectedIndex !== null ? gates?.steps[selectedIndex] : undefined
  const isIdeaMode =
    onTitleChange === undefined &&
    onDelete === undefined &&
    gates === undefined &&
    onCreateStage === undefined

  return (
    <div className="flex w-full min-w-0 flex-col">
      <WorkflowViewHeader
        title={title}
        onTitleChange={onTitleChange}
        onDelete={onDelete}
      />
      {isIdeaMode ? (
        (children ?? null)
      ) : (
        <div className="flex min-h-[250px] flex-col bg-system-background-grey">
          {gates && (
            <div className="flex flex-1 flex-col gap-2 px-4 py-3">
              <WorkflowGates
                steps={gates.steps}
                selectedIndex={gates.selectedIndex}
                onSelectGate={gates.onSelectGate}
                onDeleteGate={gates.onDeleteGate}
              />
            </div>
          )}
          {onCreateStage && (
            <div className="mt-auto flex justify-end p-4">
              <Button
                type="button"
                variant="social"
                size="default"
                onClick={onCreateStage}
              >
                <PlusIcon className="size-5" />
                Criar estágio
              </Button>
            </div>
          )}
          {step &&
            selectedIndex !== null &&
            handleCreateTask &&
            handleUpdateTask &&
            handleDeleteTask &&
            handleReorderTasks && (
              <SelectedStepContent
                key={selectedIndex}
                step={step}
                stepIndex={selectedIndex}
                onUpdateStepName={gates?.onUpdateStepName}
                onCreateTask={handleCreateTask}
                onUpdateTask={handleUpdateTask}
                onDeleteTask={handleDeleteTask}
                onUpdateTaskEndDate={
                  enableTaskEndDate ? handleUpdateTaskEndDate : undefined
                }
                onUpdateTaskResponsible={
                  enableTaskResponsible
                    ? handleUpdateTaskResponsible
                    : undefined
                }
                onReorderTasks={handleReorderTasks}
                onAddAttachments={
                  enableAttachments ? handleAddAttachments : undefined
                }
                onRemoveAttachment={
                  enableAttachments ? handleRemoveAttachment : undefined
                }
                enableAttachments={enableAttachments}
                enableTaskResponsible={enableTaskResponsible}
                enableTaskEndDate={enableTaskEndDate}
                areaIds={areaIds}
              />
            )}
        </div>
      )}
    </div>
  )
}

WorkflowView.displayName = 'WorkflowView'

export { WorkflowView }
